/**
 * Pure helpers for in-place check correction (#314).
 *
 * The editable values, and what counts as an edit, live here rather than in the
 * component so every surface that mounts the editor agrees — and so the rules
 * that decide whether we spend a payer call are testable without a DOM.
 */
import { parseFlexibleDate } from './parseFlexibleDate'
import type { EligibilityCheckDoc } from '../types'

/**
 * The minimum a surface must know about a check to offer correction (#313).
 *
 * `EligibilityCheckDoc` satisfies this structurally, but the Recovery drawer
 * holds a projection rather than the full doc — and widening the projection to
 * a whole check doc just to reuse a component would be the tail wagging the
 * dog. This is what the editor actually reads, and nothing more.
 */
export interface CorrectableCheck {
  id: string
  clinicId?: string | null
  status?: EligibilityCheckDoc['status']
  payerName?: string | null
  /** The raw shorthand the check was created from — what a clinic alias keys on. */
  requestPayerName?: string | null
  requestSubscriber?: EligibilityCheckDoc['requestSubscriber']
  identity?: EligibilityCheckDoc['identity']
}

/** The identity fields the payer is asked about — the ones that can make an
 *  answer wrong, and therefore the ones a correction may touch. */
export const CORRECTABLE_FIELDS = [
  'firstName',
  'lastName',
  'dateOfBirth',
  'memberId',
  'payerName',
] as const

export type CorrectableField = (typeof CORRECTABLE_FIELDS)[number]

export type CorrectionDraft = Record<CorrectableField, string>

/** Labels shown next to each field. */
export const FIELD_LABELS: Record<CorrectableField, string> = {
  firstName: 'First name',
  lastName: 'Last name',
  dateOfBirth: 'Date of birth',
  memberId: 'Member ID',
  payerName: 'Payer',
}

/**
 * First source that actually holds a value.
 *
 * `??` alone stops at a stored empty string, which is how a doc that carries
 * `requestSubscriber` with a blank field shadows the identity that does hold
 * one. A blank is the absence of an answer, not an answer.
 */
function firstFilled(...values: Array<string | null | undefined>): string {
  for (const v of values) {
    const t = (v ?? '').trim()
    if (t) return t
  }
  return ''
}

/**
 * The stored date of birth, as an ISO `YYYY-MM-DD` the date control can render.
 *
 * Nothing normalizes the date format on the way IN: `identity.dob` is whatever
 * the source said, so a Revolution/CSV-born check carries `"06/02/1979"` while
 * `requestSubscriber.dateOfBirth` carries `"1979-06-02"` for the same person.
 * `<input type="date">` renders only ISO and shows EMPTY for anything else —
 * which is how the recovery drawer opened a date-of-birth-mismatch case with
 * the date of birth blank (#322), the one field that case exists to correct.
 *
 * Returns `''` when the stored text is not a date we can read — see
 * `unreadableDob`, which is what the editor says so out loud.
 */
function displayableDob(stored: string): string {
  if (!stored) return ''
  return parseFlexibleDate(stored) ?? ''
}

/**
 * What the check actually asked the payer.
 *
 * `requestSubscriber` is the audit of exactly what we sent and is preferred;
 * `identity` is the set identity and is the fallback for checks written before
 * that field existed. Showing the sent values (not the patient record) is the
 * point: the operator is correcting the QUESTION, and needs to see the question
 * that was asked.
 */
export function draftFromCheck(check: CorrectableCheck): CorrectionDraft {
  const sub = check.requestSubscriber
  const id = check.identity
  return {
    firstName: firstFilled(sub?.firstName, id?.firstName),
    lastName: firstFilled(sub?.lastName, id?.lastName),
    dateOfBirth: displayableDob(firstFilled(sub?.dateOfBirth, id?.dob)),
    memberId: firstFilled(sub?.memberId, id?.memberId, id?.policyNumber),
    payerName: firstFilled(check.payerName),
  }
}

/**
 * The stored date of birth when the editor cannot show it — else null.
 *
 * A blank box reads as "we don't have her date of birth", and an operator acts
 * on that: they go hunting for a value the screen is holding, or they type one
 * from memory into the field the payer already rejected. Where the value exists
 * but cannot be rendered, the editor says which value it is, so a blank field
 * always means the same thing (we have nothing) on every surface.
 */
export function unreadableDob(check: CorrectableCheck): string | null {
  const stored = firstFilled(
    check.requestSubscriber?.dateOfBirth, check.identity?.dob,
  )
  if (!stored || parseFlexibleDate(stored)) return null
  return stored
}

/**
 * DOB compared as normalized Y-M-D, so "1980-4-4" and "1980-04-04" are the same
 * date rather than two strings. Stripping non-digits is NOT enough — it turns
 * those two into "198044" and "19800404". Anything that doesn't parse as Y-M-D
 * falls back to its raw digits, which keeps two identical odd strings equal
 * without inventing a date.
 */
function dobKey(s: string): string {
  const m = /^(\d{4})\D(\d{1,2})\D(\d{1,2})$/.exec(s.trim())
  if (!m) return s.replace(/\D/g, '')
  return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`
}

/**
 * Member IDs compared with separators stripped, mirroring what the backend
 * actually submits (`shared/memberId.ts`): cards print "0223-61-43" and payers
 * match the bare "02236143", so retyping one form as the other is not an edit
 * and must not spend a check.
 */
function memberIdKey(s: string): string {
  return s.replace(/[\s\-._/]/g, '').toUpperCase()
}

/**
 * Whether two spellings of one field ask the payer the same question.
 *
 * Exported because the card-row editor and the row builder (#349) need the
 * same rule the payload uses: an amber dot that appears for re-typing
 * `0223-61-43` as `02236143` would be marking an edit the backend is about to
 * discard as a no-op.
 */
export function sameFieldValue(field: CorrectableField, a: string, b: string): boolean {
  return sameValue(field, a, b)
}

function sameValue(field: CorrectableField, a: string, b: string): boolean {
  const x = a.trim()
  const y = b.trim()
  if (field === 'dateOfBirth') return dobKey(x) === dobKey(y)
  if (field === 'memberId') return memberIdKey(x) === memberIdKey(y)
  // Names and payer: case- and whitespace-insensitive. A payer re-picked from
  // the directory with identical text is the same question.
  return x.toLowerCase().replace(/\s+/g, ' ') === y.toLowerCase().replace(/\s+/g, ' ')
}

/**
 * Fields whose value differs from what the check sent.
 *
 * A field CLEARED to blank is not an edit. The callable drops blanks — an
 * eligibility request with an empty member id is a broken call, not a
 * correction — so a cleared field asks the payer exactly what it asked before.
 * Counting it made the panel say "1 edited" and enable Save for an empty box,
 * and the operator then got the generic "those values ask the payer the same
 * question" for what was actually nothing at all. Clearing the payer picker is
 * the way to hit this: the control blanks the name on its way to a new pick.
 */
export function changedFields(
  original: CorrectionDraft,
  draft: CorrectionDraft,
): CorrectableField[] {
  return CORRECTABLE_FIELDS.filter(
    f => draft[f].trim() !== "" && !sameValue(f, original[f], draft[f]),
  )
}

/** True when there is typed work that would be lost by closing. */
export function isDirty(original: CorrectionDraft, draft: CorrectionDraft): boolean {
  return changedFields(original, draft).length > 0
}

/**
 * The payload for the callable: only what changed. A field the operator did not
 * touch is omitted entirely rather than sent as its current value, so the
 * backend's own no-change comparison sees the same picture this screen does.
 */
export function correctionPayload(
  original: CorrectionDraft,
  draft: CorrectionDraft,
): Partial<Record<CorrectableField, string>> {
  const out: Partial<Record<CorrectableField, string>> = {}
  for (const f of changedFields(original, draft)) out[f] = draft[f].trim()
  return out
}

/** The directory entry behind a corrected payer name (#349). */
export interface PayerPick {
  tradingPartnerServiceId: string
  stediId: string | null
}

/** What the callable receives: the changed fields, plus a payer pick's ids. */
export type CorrectionPayload = Partial<Record<CorrectableField, string>> & {
  payerTradingPartnerServiceId?: string
  payerStediId?: string
}

/**
 * Attach a directory pick's ids to the payload (#349).
 *
 * The backend treats a payer that arrives WITH ids as human-picked — the rule
 * `resolveRoute` already applies to the manual form and the CSV grid: no
 * clinic alias, no Medicare classifier, no usage ranking runs on the name,
 * and the new check stores the name the operator chose. Sent name-only, the
 * pick was re-resolved from scratch and an alias could hand back a different
 * payer, which also left the row reading as edited once the new check's
 * stored name came back. A name without a pick (the SMS loop's card read)
 * still resolves fresh. Nothing is attached unless the payer actually
 * changed: a pick that left it as it was sends nothing, like any other
 * untouched field.
 */
export function withPayerPick(
  payload: Partial<Record<CorrectableField, string>>,
  pick: PayerPick | null,
): CorrectionPayload {
  if (!payload.payerName || !pick) return payload
  return {
    ...payload,
    payerTradingPartnerServiceId: pick.tradingPartnerServiceId,
    ...(pick.stediId ? { payerStediId: pick.stediId } : {}),
  }
}

/**
 * Whether correcting this check is offered at all.
 *
 * Everything except a check still in flight: a pending check has no answer to
 * disagree with yet, and firing a correction against it would race its own
 * result. Failures, `auto_retry` (a human who can see the answer should not
 * wait out a retry ladder) and — the dangerous one — SUCCESSES all qualify.
 */
export function canCorrect(check: CorrectableCheck): boolean {
  return check.status !== 'pending'
}

/**
 * Whether a corrected payer is worth offering as a clinic-wide mapping.
 *
 * Only when the payer itself changed: correcting a member id says nothing about
 * what the clinic's shorthand should resolve to. The offer is always a separate
 * confirmation — a check-level fix must never silently become a clinic rule.
 */
export function offersPayerMapping(
  original: CorrectionDraft,
  draft: CorrectionDraft,
): boolean {
  return changedFields(original, draft).includes('payerName')
}
